/* Sticky add-to-cart bar.

   The bar carries no cart logic of its own: its button is wired to the
   product form already on the page with the `form` attribute, so the theme's
   product-form element (and the cart drawer behind it) handle the submit.
   This file only mirrors the real button's state and shows or hides the bar. */

class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.bar = this;
    this.button = this.querySelector('[data-satc-button]');
    this.labelEl = this.querySelector('[data-satc-label]');
    this.priceEl = this.querySelector('[data-satc-price]');
    this.variantEl = this.querySelector('[data-satc-variant]');
    this.sizeSelect = this.querySelector('[data-satc-size]');

    this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    this.priceSource = document.getElementById(`price-${this.sectionId}`);
    this.form = document.getElementById(`product-form-${this.sectionId}`);

    if (!this.mainButton || !this.form) return;

    this.onScroll = this.onScroll.bind(this);
    this.syncState = this.syncState.bind(this);
    this.onSizeChange = this.onSizeChange.bind(this);
    if (this.sizeSelect) this.sizeSelect.addEventListener('change', this.onSizeChange);

    this.hidden = false;
    this.watchMainButton();
    this.watchState();
    this.syncState();
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.sizeSelect) this.sizeSelect.removeEventListener('change', this.onSizeChange);
    if (this.observer) this.observer.disconnect();
    if (this.stateObserver) this.stateObserver.disconnect();
    if (this.priceObserver) this.priceObserver.disconnect();
    document.body.classList.remove('satc-open');
  }

  /* The bar appears once the real button has left the viewport. */
  watchMainButton() {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        this.mainButtonVisible = entries[0].isIntersecting;
        this.onScroll();
      },
      { rootMargin: '0px 0px -20px 0px' }
    );
    this.observer.observe(this.mainButton);
  }

  /* The theme re-renders the button and the price when the variant changes. */
  watchState() {
    if (!('MutationObserver' in window)) return;
    if (this.stateObserver) this.stateObserver.disconnect();
    if (this.priceObserver) this.priceObserver.disconnect();

    this.stateObserver = new MutationObserver(this.syncState);
    this.stateObserver.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'data-preorder'],
      childList: true,
      subtree: true,
      characterData: true,
    });

    if (this.priceSource) {
      this.priceObserver = new MutationObserver(this.syncState);
      this.priceObserver.observe(this.priceSource, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  syncState() {
    if (!document.contains(this.mainButton)) {
      const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!button) return;
      this.mainButton = button;
      this.priceSource = document.getElementById(`price-${this.sectionId}`);
      this.watchState();
    }

    const soldOut = this.mainButton.hasAttribute('disabled');
    this.button.disabled = soldOut;

    if (soldOut) {
      const mainText = this.mainButton.querySelector('span');
      this.labelEl.textContent = mainText ? mainText.textContent.trim() : 'Sold out';
    }

    const isPreorder = !soldOut && this.mainButton.getAttribute('data-preorder') === 'true';
    this.button.toggleAttribute('data-preorder', isPreorder);

    if (soldOut) {
      /* label already set above */
    } else if (isPreorder) {
      const mainText = this.mainButton.querySelector('span');
      this.labelEl.textContent = mainText ? mainText.textContent.trim() : 'Pre-order';
    } else {
      this.labelEl.textContent = this.dataset.label || 'Add to cart';
    }

    const btnPrice = this.querySelector('[data-satc-btn-price]');
    if (btnPrice) btnPrice.hidden = soldOut;

    if (this.priceSource) {
      /* The theme prints the current price in .price__regular as the last
         regular item, whether or not the variant is on sale. */
      const items = this.priceSource.querySelectorAll('.price__regular .price-item--regular');
      const price = items[items.length - 1];
      if (price && price.textContent.trim() !== '') {
        const text = price.textContent.trim();
        if (this.priceEl) this.priceEl.textContent = text;
        if (btnPrice) btnPrice.textContent = text;
      }
    }

    if (this.variantEl) {
      const title = this.selectedVariantTitle();
      if (title) this.variantEl.textContent = title;
    }

    /* Keep the sticky size dropdown in step with the main picker. */
    if (this.sizeSelect) {
      const picker = this.form.closest('product-info') || document;
      const selected = new Set();
      picker.querySelectorAll('variant-selects fieldset input[type="radio"]:checked').forEach((i) => selected.add(i.value));
      picker.querySelectorAll('variant-selects select').forEach((s) => { if (s.value) selected.add(s.value); });
      Array.prototype.forEach.call(this.sizeSelect.options, (o) => {
        if (selected.has(o.value)) this.sizeSelect.value = o.value;
      });
    }
  }

  /* Reads the chosen options straight out of the variant picker. */
  selectedVariantTitle() {
    const picker = this.form.closest('product-info') || document;
    const values = [];

    picker.querySelectorAll('variant-selects fieldset input[type="radio"]:checked').forEach((input) => {
      values.push(input.value);
    });

    if (values.length === 0) {
      picker.querySelectorAll('variant-selects select').forEach((select) => {
        if (select.value) values.push(select.value);
      });
    }

    return values.join(' / ');
  }

  /* Picking a size in the bar drives the real variant picker, so the theme
     updates the form variant, price and availability itself. */
  onSizeChange() {
    const value = this.sizeSelect.value;
    if (!value) return;
    const picker = this.form.closest('product-info') || document;
    let changed = false;

    picker.querySelectorAll('variant-selects fieldset input[type="radio"]').forEach((input) => {
      if (changed || input.value !== value) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      changed = true;
    });

    if (!changed) {
      picker.querySelectorAll('variant-selects select').forEach((select) => {
        if (changed) return;
        const match = Array.prototype.some.call(select.options, (o) => o.value === value);
        if (!match) return;
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        changed = true;
      });
    }
  }

  onScroll() {
    const scrolled = window.scrollY > 240;
    const show = scrolled && this.mainButtonVisible === false;

    if (show === this.isVisible) return;
    this.isVisible = show;
    this.bar.classList.toggle('satc--visible', show);

    if (show) {
      document.documentElement.style.setProperty('--satc-height', `${this.offsetHeight}px`);
      document.body.classList.add('satc-open');
    } else {
      document.body.classList.remove('satc-open');
    }
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAddToCart);
}
